import { ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useMemo, useState } from 'react';
import { ConfirmDialog } from '../../../components/Modal';
import { T, useT } from '../../../translation/src';
import { listRules, removeRules, type ObjectRule, type RuleKind } from '../document/rules';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { PanelButton, PanelSection } from './fields';

/** A colour dot per kind, so sections are told apart at a glance. */
const KIND_STYLE: Record<RuleKind, string> = {
  value: 'bg-sky-500',
  link: 'bg-french-violet-500',
  handle: 'bg-neon-green-500',
  group: 'bg-amber-500',
  shape: 'bg-gray-400',
};
const KIND_ORDER: readonly RuleKind[] = ['link', 'handle', 'group', 'shape', 'value'];

/**
 * The object's rules — what makes it adapt beyond plain drawing (made by
 * pinned points and the AI) — as a plain list named by what each one
 * IS. No formulas here: the AI writes those. Select any number and delete
 * them; the drawing keeps its current look (document/rules.ts).
 */
const RulesPanel_: React.FC = () => {
  const { store, ui } = useObjectEditor();
  const { spec } = useEditorState();
  const rules = useMemo(() => listRules(spec), [spec]);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [openKinds, setOpenKinds] = useState<ReadonlySet<RuleKind>>(new Set());
  // Rules can disappear (undo, AI changes): only ids that still exist count.
  const selected = rules.filter((r) => picked.has(r.id));
  /** Section titles: what the rules in it are. */
  const kindLabel: Record<RuleKind, string> = {
    value: useT('computed sizes', { swc: true }),
    link: useT('linked settings', { swc: true }),
    handle: useT('handles', { swc: true }),
    group: useT('placed groups', { swc: true }),
    shape: useT('shapes that follow the size', { swc: true }),
  };
  const words = {
    linkedDepth: useT('linked depth', { swc: true }),
    runEnds: useT('run end handles', { swc: true }),
    handleOf: useT('handle', { swc: false }),
  };
  const nameOf = (rule: ObjectRule): string => {
    if (rule.label) return rule.label;
    if (rule.id === 'depth') return words.linkedDepth;
    if (rule.id === 'ends') return words.runEnds;
    if (rule.kind === 'handle') return `${rule.subject} ${words.handleOf}`;
    return rule.subject;
  };
  const readOnly = !!ui.preview || ui.mode === 'test';

  if (rules.length === 0) return null;

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allPicked = selected.length === rules.length;

  return (
    <PanelSection
      title={<T _str="rules ({count})" args={{ count: rules.length }} />}
      collapsible
      actions={
        selected.length > 0 && !readOnly ? (
          <PanelButton danger onClick={() => setConfirming(true)}>
            <TrashIcon className="h-3.5 w-3.5" />
            <T _str="delete {count}" args={{ count: selected.length }} swc />
          </PanelButton>
        ) : undefined
      }
    >
      <p className="text-xs text-gray-500 dark:text-gray-400">
        <T _str="what makes this object adapt when it's resized or its settings change. Pinned points add them." swc />
      </p>
      {!readOnly && (
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(rules.map((r) => r.id)))} className="accent-french-violet-600" />
          <T _str="select all" swc />
        </label>
      )}
      {/* One collapsed section per kind: a count and a checkbox for the lot; open it for the single rules. */}
      <ul className="flex flex-col gap-1">
        {KIND_ORDER.filter((kind) => rules.some((r) => r.kind === kind)).map((kind) => {
          const inKind = rules.filter((r) => r.kind === kind);
          const pickedInKind = inKind.filter((r) => picked.has(r.id)).length;
          const open = openKinds.has(kind);
          return (
            <li key={kind} className="rounded-md border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 px-2 py-1">
                {!readOnly && (
                  <input
                    type="checkbox"
                    checked={pickedInKind === inKind.length}
                    ref={(el) => {
                      if (el) el.indeterminate = pickedInKind > 0 && pickedInKind < inKind.length;
                    }}
                    onChange={() =>
                      setPicked((current) => {
                        const next = new Set(current);
                        const all = pickedInKind === inKind.length;
                        for (const r of inKind) if (all) next.delete(r.id);
                        else next.add(r.id);
                        return next;
                      })
                    }
                    aria-label={kindLabel[kind]}
                    className="accent-french-violet-600"
                  />
                )}
                <button
                  type="button"
                  onClick={() =>
                    setOpenKinds((current) => {
                      const next = new Set(current);
                      if (next.has(kind)) next.delete(kind);
                      else next.add(kind);
                      return next;
                    })
                  }
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm"
                >
                  <span className={classNames('h-2 w-2 shrink-0 rounded-full', KIND_STYLE[kind])} />
                  <span className="truncate">{kindLabel[kind]}</span>
                  <span className="text-xs text-gray-500">{inKind.length}</span>
                  <ChevronRightIcon className={classNames('ml-auto h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform', open && 'rotate-90')} />
                </button>
              </div>
              {open && (
                <ul className="flex flex-col gap-0.5 border-t border-gray-200 px-2 py-1 dark:border-gray-700">
                  {inKind.map((rule) => (
                    <li key={rule.id} className={classNames('flex items-center gap-2 rounded px-1 py-0.5', picked.has(rule.id) && 'bg-french-violet-600/10')}>
                      {!readOnly && <input type="checkbox" checked={picked.has(rule.id)} onChange={() => toggle(rule.id)} aria-label={nameOf(rule)} className="accent-french-violet-600" />}
                      <button
                        type="button"
                        disabled={!rule.target}
                        onClick={() => rule.target && store.select(rule.target.kind === 'shape' ? { kind: 'shapes', ids: [rule.target.id] } : { kind: 'group', id: rule.target.id })}
                        className="min-w-0 flex-1 truncate text-left text-sm enabled:hover:underline disabled:cursor-default"
                        title={nameOf(rule)}
                      >
                        {nameOf(rule)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          store.apply((s) => removeRules(s, selected.map((r) => r.id)));
          setPicked(new Set());
        }}
        variant="warning"
        title={<T _str="delete {count} rules?" args={{ count: selected.length }} swc />}
        message={
          <span className="flex flex-col gap-2">
            <span>
              <T _str="the drawing keeps how it looks now; placed objects stop adapting in these ways. You can undo this." swc />
            </span>
            <span className="max-h-40 overflow-y-auto text-xs text-gray-600 dark:text-gray-300">{selected.map(nameOf).join(', ')}</span>
          </span>
        }
        confirmLabel={<T _str="delete" swc />}
      />
    </PanelSection>
  );
};

export const RulesPanel = memo(RulesPanel_);
